import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { sortBy, cloneDeep, snakeCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { useSelector, useDispatch } from 'react-redux';
import { updateUserPreferencesAsync } from '../../redux/user/Actions';

import { makeStyles } from '@material-ui/core/styles';
import { Grid, MenuItem, Tooltip, Typography } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Skeleton from '@material-ui/lab/Skeleton';

import ScrollableContainer from '../utility/ScrollableContainer';
import Header from '../Common/UI/Header';
import SelectTextField from '../Common/UI/SelectTextField';

const useStyles = makeStyles(theme => style(theme));

const propTypes = {};

const defaultProps = {};
function Preferences() {
    const classes = useStyles();

    const { prefs, isUserUpdating, features } = useSelector(state => state.user);
    const analysisProfiles = useSelector(state => state.lookups.uiData.analysisProfiles);

    const layers = useSelector(state => state.layers.layers);

    const userAnalysisProfiles = Boolean(prefs.defaultAnalysisProfiles)
        ? cloneDeep(prefs.defaultAnalysisProfiles)
        : [];

    const dispatch = useDispatch();
    const dispatchUpdateAnalysisProfilePref = (newPrefs, newPrefSnakeCase) =>
        dispatch(
            updateUserPreferencesAsync(
                {},
                { defaultAnalysisProfiles: newPrefs },
                {
                    default_analysis_profiles: newPrefSnakeCase
                }
            )
        );

    const dispatchUpdateAreaUnitPref = unit => {
        dispatch(
            updateUserPreferencesAsync({}, { defaultAreaUnit: unit }, { default_area_unit: unit })
        );
    };

    const defaultLayersAndProfiles = cloneDeep(features.analysis.layers);

    const userProfiles = useMemo(() => {
        return processPref(userAnalysisProfiles, defaultLayersAndProfiles);
    }, [prefs.defaultAnalysisProfiles, layers]);

    function processPref(profiles, defaultLayersAndProfiles) {
        defaultLayersAndProfiles.forEach(layer => {
            const idx = profiles.findIndex(ele => ele.layerId === layer.layerId);

            if (idx === -1) {
                profiles.push({
                    layerId: layer.layerId,
                    profileId: layer.profiles[0],
                    profiles: processProfiles(layer, analysisProfiles)
                });
            } else {
                profiles[idx].profiles = processProfiles(layer, analysisProfiles);
            }
        });

        const sortedProfiles = sortBy(profiles, [
            function (item) {
                return item.layerId;
            }
        ]);

        sortedProfiles.forEach((profile, index) => {
            const layer = layers.find(layer => layer.id === profile.layerId);

            sortedProfiles[index].layerName = layer?.name;
        });
        return sortedProfiles;
    }

    function processProfiles(layer, profiles) {
        const newProfiles = layer.profiles.map(profileId => {
            const found = profiles.find(aProf => aProf.id === profileId);

            if (found) return { id: found.id, name: found.name };
        });
        return newProfiles;
    }

    const removeDuplicates = currentData => {
        const seenLayerIds = new Set();
        const uniqueArray = [];

        currentData.forEach(item => {
            if (!seenLayerIds.has(item.layerId)) {
                seenLayerIds.add(item.layerId);
                uniqueArray.push(item);
            }
        });

        return uniqueArray;
    };

    function convertToSnakeCase(currentData) {
        const results = currentData.map(item => {
            const layerSnake = snakeCase('layerId');
            const profileSnake = snakeCase('profileId');

            return {
                [layerSnake]: item.layerId,
                [profileSnake]: item.profileId
            };
        });

        return results;
    }

    function handleSelect(layerId, profileId) {
        const oldPrefs = Boolean(prefs.defaultAnalysisProfiles)
            ? cloneDeep(prefs.defaultAnalysisProfiles)
            : [];
        oldPrefs.unshift({ layerId, profileId });
        const newPrefs = removeDuplicates(oldPrefs);
        const newPrefSnakeCase = convertToSnakeCase(newPrefs);

        dispatchUpdateAnalysisProfilePref(newPrefs, newPrefSnakeCase);
    }

    const loading = userProfiles.map(item => (
        <Grid item xs={12} lg={7} key={uuidv4()}>
            <div className={classes.containerLoading}>
                <div style={{ width: '300px' }}>
                    <Skeleton variant="text" width={'90%'} height={30} />
                    <Skeleton variant="text" width={'90%'} height={30} />
                </div>
                <Skeleton variant="rect" animation="wave" width={'90%'} height={48} />
            </div>
        </Grid>
    ));

    return (
        <div className={classes.root}>
            <Header>
                <Typography variant="h2" color="textPrimary">
                    Preferences
                </Typography>
            </Header>
            <ScrollableContainer className={classes.page} grow>
                <Grid container spacing={2}>
                    {layers.length === 0 ? (
                        loading
                    ) : (
                        <Fragment>
                            {userProfiles.map(item => (
                                <Grid item xs={12} lg={7} key={uuidv4()}>
                                    <div className={classes.container}>
                                        <Typography className={classes.label} color="textPrimary">
                                            Default Analysis Profile for {item.layerName}
                                        </Typography>
                                        <SelectTextField
                                            value={item.profileId}
                                            onChange={e =>
                                                handleSelect(item.layerId, e.target.value)
                                            }>
                                            {item.profiles.map(profile => (
                                                <MenuItem key={profile.id} value={profile.id}>
                                                    {profile.name}
                                                </MenuItem>
                                            ))}
                                        </SelectTextField>
                                    </div>
                                </Grid>
                            ))}
                            <Grid item xs={12} lg={7}>
                                <div className={classes.container}>
                                    <Typography className={classes.label} color="textPrimary">
                                        Default Area Unit
                                        <Tooltip
                                            arrow
                                            title={
                                                'This is default unit that is shown when viewing area units in places where there is no selector'
                                            }
                                            placement={'right'}>
                                            <InfoOutlinedIcon />
                                        </Tooltip>
                                    </Typography>
                                    <SelectTextField
                                        value={prefs?.defaultAreaUnit || 'ha'}
                                        onChange={e => dispatchUpdateAreaUnitPref(e.target.value)}>
                                        <MenuItem value="ac">{'Acres'}</MenuItem>
                                        <MenuItem value="ha">{'Hectares'}</MenuItem>
                                        <MenuItem value="m2">{'m²'}</MenuItem>
                                        <MenuItem value="sqft">{'sqft'}</MenuItem>
                                    </SelectTextField>
                                </div>
                            </Grid>
                        </Fragment>
                    )}
                </Grid>
            </ScrollableContainer>
        </div>
    );
}

Preferences.propTypes = propTypes;
Preferences.defaultProps = defaultProps;

const style = theme => ({
    root: {
        backgroundColor: theme.palette.background.main
    },
    page: {
        padding: 60,
        '&> *:not(:first-child)': {
            marginTop: 30
        }
    },

    container: {
        display: 'flex',
        alignItems: 'center',
        '&> *:not(:last-child)': {
            marginRight: 100
        },
        '&> div': {
            flexGrow: 1
        }
    },
    containerLoading: {
        display: 'flex',
        alignItems: 'center',
        '&> *:not(:last-child)': {
            marginRight: 100
        },
        '&> div': {
            flexGrow: 1
        },
        marginBottom: 5
    },
    label: {
        flexShrink: 0,
        width: '300px',

        display: 'flex',
        alignItems: 'center',
        '&> svg': {
            fontSize: '1em',
            marginLeft: '6px'
        }
    }
});

export default Preferences;
